import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { Perspective } from '@features/Perspective/types/perspective.types';
import { useSettingsSafe } from '@shared/contexts/SettingsContext';

interface EEGConfiguration {
  config_id: string;
  config_name: string;
  montage_id: string;
  sensitivity_value: number;
  time_scale: number;
  high_pass_filter: number;
  low_pass_filter: number;
  notch_filter_60hz: boolean;
  notch_filter_50hz?: boolean;
  reference_type: string;
  playback_speed: number;
  is_default: boolean;
}

interface PerspectiveState {
  activePerspectiveId: string | null;
  activePerspective: Perspective | null;
  isClinicalMode: boolean;
  montageLocked: boolean;
  filtersLocked: boolean;
  draftConfig: Partial<EEGConfiguration> | null;
  hasUnappliedChanges: boolean;
  schemaVersion: string;
  lastAppliedAt: Date | null;
}

interface PerspectiveStateContextType {
  state: PerspectiveState;
  setActivePerspective: (perspective: Perspective) => void;
  applyPerspective: (perspective: Perspective) => Promise<void>;
  setDraftConfig: (config: Partial<EEGConfiguration>) => void;
  applyDraftConfig: () => void;
  discardDraft: () => void;
  toggleMontageLock: () => void;
  toggleFiltersLock: () => void;
  setClinicalMode: (enabled: boolean) => void;
}

const PerspectiveStateContext = createContext<PerspectiveStateContextType | undefined>(undefined);

const STORAGE_KEY_LOCKS = 'perspectiveState_locks';
const STORAGE_KEY_MODE = 'perspectiveState_clinicalMode';
const REVIEW_MODE_PREFERENCE_KEY = 'eegReviewMode';

export const PerspectiveStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { settings, loading: settingsLoading, updateApplicationSettings } = useSettingsSafe();
  const hasLoadedBackendModeRef = useRef(false);
  const lastPersistedModeRef = useRef<boolean | null>(null);
  // Load initial state from localStorage
  const loadLocksFromStorage = (): { montageLocked: boolean; filtersLocked: boolean } => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_LOCKS);
      return stored ? JSON.parse(stored) : { montageLocked: false, filtersLocked: false };
    } catch {
      return { montageLocked: false, filtersLocked: false };
    }
  };

  const loadModeFromStorage = (): boolean => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_MODE);
      if (stored === null) return true;
      return stored === 'true';
    } catch {
      return true;
    }
  };

  const initialLocks = loadLocksFromStorage();
  const initialMode = loadModeFromStorage();

  const [state, setState] = useState<PerspectiveState>({
    activePerspectiveId: null,
    activePerspective: null,
    isClinicalMode: initialMode,
    montageLocked: initialLocks.montageLocked,
    filtersLocked: initialLocks.filtersLocked,
    draftConfig: null,
    hasUnappliedChanges: false,
    schemaVersion: '2.0',
    lastAppliedAt: null,
  });

  // Persist locks to localStorage when changed
  useEffect(() => {
    if (state.isClinicalMode) {
      localStorage.setItem(
        STORAGE_KEY_LOCKS,
        JSON.stringify({
          montageLocked: state.montageLocked,
          filtersLocked: state.filtersLocked,
        })
      );
    }
  }, [state.montageLocked, state.filtersLocked, state.isClinicalMode]);

  // Persist mode to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_MODE, String(state.isClinicalMode));
  }, [state.isClinicalMode]);

  useEffect(() => {
    if (settingsLoading || hasLoadedBackendModeRef.current) return;
    const customPreferences =
      settings.custom_preferences && typeof settings.custom_preferences === 'object'
        ? (settings.custom_preferences as Record<string, unknown>)
        : {};
    const backendMode = customPreferences[REVIEW_MODE_PREFERENCE_KEY];
    if (backendMode === 'clinical' || backendMode === 'research') {
      const backendClinicalMode = backendMode === 'clinical';
      lastPersistedModeRef.current = backendClinicalMode;
      hasLoadedBackendModeRef.current = true;
      setState((prev) => ({
        ...prev,
        isClinicalMode: backendClinicalMode,
        montageLocked: backendClinicalMode ? prev.montageLocked : false,
        filtersLocked: backendClinicalMode ? prev.filtersLocked : false,
      }));
      return;
    }
    hasLoadedBackendModeRef.current = true;
  }, [settings.custom_preferences, settingsLoading]);

  useEffect(() => {
    if (!hasLoadedBackendModeRef.current) return;
    if (lastPersistedModeRef.current === state.isClinicalMode) return;

    lastPersistedModeRef.current = state.isClinicalMode;
    const customPreferences =
      settings.custom_preferences && typeof settings.custom_preferences === 'object'
        ? (settings.custom_preferences as Record<string, unknown>)
        : {};
    void updateApplicationSettings({
      custom_preferences: {
        ...customPreferences,
        [REVIEW_MODE_PREFERENCE_KEY]: state.isClinicalMode ? 'clinical' : 'research',
        eegReviewModeUpdatedAt: new Date().toISOString(),
      },
    }).catch(() => {
      lastPersistedModeRef.current = null;
    });
  }, [settings.custom_preferences, state.isClinicalMode, updateApplicationSettings]);

  const setActivePerspective = useCallback((perspective: Perspective) => {
    setState((prev) => ({
      ...prev,
      activePerspectiveId: perspective.perspective_id || null,
      activePerspective: perspective,
    }));
  }, []);

  const applyPerspective = useCallback(async (perspective: Perspective) => {
    // Convert perspective config to EEG configuration format
    const draftFromPerspective: Partial<EEGConfiguration> = {
      montage_id: perspective.montage_id,
      high_pass_filter: perspective.filter_config?.highPassFilter,
      low_pass_filter: perspective.filter_config?.lowPassFilter,
      notch_filter_60hz: perspective.filter_config?.notchFilter === 60,
      notch_filter_50hz: perspective.filter_config?.notchFilter === 50,
      sensitivity_value:
        typeof perspective.clinical_defaults?.sensitivityUvPerCm === "number"
          ? perspective.clinical_defaults.sensitivityUvPerCm / 10
          : undefined,
      time_scale: perspective.time_window_config?.duration,
      // Other mappings as needed
    };

    setState((prev) => ({
      ...prev,
      activePerspectiveId: perspective.perspective_id || null,
      activePerspective: perspective,
      draftConfig: draftFromPerspective,
      hasUnappliedChanges: true,
    }));
  }, []);

  const setDraftConfig = useCallback(
    (config: Partial<EEGConfiguration>) => {
      // Validate against locks
      if (state.montageLocked && config.montage_id !== undefined) {
        throw new Error('Montage is locked. Unlock to make changes.');
      }

      if (
        state.filtersLocked &&
        (config.high_pass_filter !== undefined ||
          config.low_pass_filter !== undefined ||
          config.notch_filter_60hz !== undefined ||
          config.notch_filter_50hz !== undefined)
      ) {
        throw new Error('Filters are locked. Unlock to make changes.');
      }

      setState((prev) => ({
        ...prev,
        draftConfig: { ...prev.draftConfig, ...config },
        hasUnappliedChanges: true,
      }));
    },
    [state.montageLocked, state.filtersLocked]
  );

  const applyDraftConfig = useCallback(() => {
    setState((prev) => ({
      ...prev,
      hasUnappliedChanges: false,
      lastAppliedAt: new Date(),
    }));
  }, []);

  const discardDraft = useCallback(() => {
    setState((prev) => ({
      ...prev,
      draftConfig: null,
      hasUnappliedChanges: false,
    }));
  }, []);

  const toggleMontageLock = useCallback(() => {
    setState((prev) => ({
      ...prev,
      montageLocked: !prev.montageLocked,
    }));
  }, []);

  const toggleFiltersLock = useCallback(() => {
    setState((prev) => ({
      ...prev,
      filtersLocked: !prev.filtersLocked,
    }));
  }, []);

  const setClinicalMode = useCallback((enabled: boolean) => {
    setState((prev) => ({
      ...prev,
      isClinicalMode: enabled,
      // Reset locks when switching modes
      montageLocked: enabled ? prev.montageLocked : false,
      filtersLocked: enabled ? prev.filtersLocked : false,
    }));
  }, []);

  const value: PerspectiveStateContextType = {
    state,
    setActivePerspective,
    applyPerspective,
    setDraftConfig,
    applyDraftConfig,
    discardDraft,
    toggleMontageLock,
    toggleFiltersLock,
    setClinicalMode,
  };

  return <PerspectiveStateContext.Provider value={value}>{children}</PerspectiveStateContext.Provider>;
};

export const usePerspectiveState = (): PerspectiveStateContextType => {
  const context = useContext(PerspectiveStateContext);
  if (!context) {
    throw new Error('usePerspectiveState must be used within a PerspectiveStateProvider');
  }
  return context;
};

const OPTIONAL_PERSPECTIVE_STATE: PerspectiveState = {
  activePerspectiveId: null,
  activePerspective: null,
  isClinicalMode: false,
  montageLocked: false,
  filtersLocked: false,
  draftConfig: null,
  hasUnappliedChanges: false,
  schemaVersion: 'optional',
  lastAppliedAt: null,
};

/** Safe for shared Header when ClinicalProviders omit PerspectiveStateProvider (e.g. /worklist LCP). */
export const useOptionalPerspectiveState = (): PerspectiveStateContextType => {
  const context = useContext(PerspectiveStateContext);
  if (context) return context;
  return {
    state: OPTIONAL_PERSPECTIVE_STATE,
    setActivePerspective: () => undefined,
    applyPerspective: async () => undefined,
    setDraftConfig: () => undefined,
    applyDraftConfig: () => undefined,
    discardDraft: () => undefined,
    toggleMontageLock: () => undefined,
    toggleFiltersLock: () => undefined,
    setClinicalMode: () => undefined,
  };
};
